import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HOW_IT_WORKS } from '../../utils/constants';
import { fadeUp, spring, stagger, viewportOnce } from '../../utils/animations';

const icons: Record<string, JSX.Element> = {
  mic: (
    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
    </svg>
  ),
  waveform: (
    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h.75m3-3v6m3-9v12m3-9v6m3-3h.75" />
    </svg>
  ),
  chart: (
    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.281m5.94 2.28l-2.28 5.941" />
    </svg>
  ),
};

/* Animated connector line between steps */
function Connector({ inView }: { inView: boolean }) {
  return (
    <div className="hidden md:block absolute top-14 left-[calc(50%+40px)] w-[calc(100%-80px)] h-px overflow-hidden">
      <motion.div
        className="h-full bg-gradient-to-r from-white/10 to-white/[0.03]"
        initial={{ scaleX: 0, originX: 0 }}
        animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
      />
    </div>
  );
}

export default function HowItWorks() {
  const [ref, inView] = useInView(viewportOnce);

  return (
    <section className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern" />

      <div className="container-custom relative z-10" ref={ref}>
        {/* Headline */}
        <motion.h2
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-center mb-20 text-white"
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          transition={spring}
        >
          {HOW_IT_WORKS.headline}
        </motion.h2>

        {/* Steps — staggered */}
        <motion.div
          className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          variants={stagger(0.18, 0.25)}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {HOW_IT_WORKS.steps.map((step, i) => (
            <motion.div
              key={step.number}
              className="relative group"
              variants={fadeUp}
              transition={spring}
            >
              {i < HOW_IT_WORKS.steps.length - 1 && <Connector inView={inView} />}

              <div className="glass-card p-8 h-full transition-colors duration-300 hover:border-white/[0.15]">
                {/* Step number */}
                <div className="text-xs font-mono text-brand-purple/50 mb-4 tracking-wider">
                  {step.number}
                </div>

                {/* Icon */}
                <motion.div
                  className="w-14 h-14 rounded-2xl bg-gradient-brand flex items-center justify-center text-white mb-6 gpu"
                  whileHover={{ scale: 1.08 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  {icons[step.icon]}
                </motion.div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>

                {/* Description */}
                <p className="text-white/45 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
